import type { RouteLocationRaw } from "vue-router";
import {
  DC_AI_COCKPIT_CONCEPT,
  DC_DATAPIXIE_CONCEPT,
  DC_FERATEL_CONCEPT,
  type ProductFamily,
} from "@ai-platform/contracts";

/*
 * How the catalogue shows a product family (design of 2026-10-13, B-61 to B-64; overview of the
 * prototype of 2026-10-29): the order of the shelves, the plural heading of a shelf, the label above
 * a card's title, the mark beside the heading, the family's logo on the band of its cards, the
 * columns of the shelf, the family's own page and where its cards lead (ADR-150 points 4 and 5).
 * Display configuration of the portal, in its own code like
 * the texts per reason code (TL and developer, 2026-10-20; the portal gets a config of its own
 * after the alpha release, this table moves there). By the concept's id in "Produkttypen" from
 * the gateway contract, never by its label, which marketing may change (ADR-112). What dataCycle
 * knows stays there. A family not in the table gets its prefLabel from the API as heading and
 * card label and two columns.
 */

/** The marks the table may name, each a component of `components/catalog/catalog-logos.ts`. */
export type CatalogLogo = "ai-cockpit" | "feratel" | "feratel-logo" | "feratel-ai-plus" | "datacycle" | "datapixie";

export type Columns = 1 | 2 | 3 | 4;

/**
 * The family's own page (ADR-150 point 4): the route's name and the shelf head's way to it, as the
 * prototype's overview ("Zu den feratel Agenten").
 */
export type FamilyPage = { route: string; more: string | null };

/** How a shelf and its cards look. */
export type FamilyDisplay = {
  heading: string;
  /** above a card's title, as the prototype's `FAMILY`; null takes the family's label from the API */
  label: string | null;
  headingIcon: CatalogLogo | null;
  bandLogo: CatalogLogo | null;
  columns: Columns;
  /** the family's own page; null without one */
  page: FamilyPage | null;
  /**
   * where a card leads: the product's page, or the family's page, as the dataPixies' bundles,
   * which have no page of their own yet (ADR-150 "Nicht Teil"; they will, TL 2026-10-29)
   */
  cards: "product" | "page";
  /**
   * the route of the family's detail page of a product under `/catalog/<family>/<slug>`, the
   * product's page for a card leading to it (ADR-150 addendum of 2026-10-29); null: the generic
   * `/catalog/:slug`
   */
  detail: string | null;
};

/** The families with a look of their own, in the order of their shelves. */
export const FAMILY_DISPLAY: ReadonlyMap<string, FamilyDisplay> = new Map<string, FamilyDisplay>([
  [
    DC_AI_COCKPIT_CONCEPT,
    {
      heading: "AI Cockpit",
      label: null,
      headingIcon: null,
      bandLogo: null,
      columns: 1,
      // the slab leads to the page itself ("Ansehen", `AiCockpitHero`)
      page: { route: "aiCockpit", more: null },
      cards: "page",
      detail: null,
    },
  ],
  [
    DC_FERATEL_CONCEPT,
    {
      heading: "feratel Agenten",
      label: "feratel Agent",
      headingIcon: "feratel",
      bandLogo: "feratel-logo",
      columns: 3,
      page: { route: "feratelAgents", more: "Zu den feratel Agenten" },
      cards: "product",
      detail: "feratelAgent",
    },
  ],
  [
    DC_DATAPIXIE_CONCEPT,
    {
      heading: "dataPixies",
      label: "dataPixies",
      headingIcon: "datapixie",
      bandLogo: null,
      columns: 4,
      page: { route: "dataPixies", more: "Zu den dataPixies" },
      cards: "page",
      detail: null,
    },
  ],
]);

/** The concept ids in the table's order: the order of the shelves (`inShelfOrder`). */
export const CONFIGURED_FAMILIES: readonly string[] = [...FAMILY_DISPLAY.keys()];

/** How a family not in the table looks: no marks, two columns, no page, cards to their products. */
export const DEFAULT_DISPLAY: Omit<FamilyDisplay, "heading"> = {
  label: null,
  headingIcon: null,
  bandLogo: null,
  columns: 2,
  page: null,
  cards: "product",
  detail: null,
};

/**
 * Where a product's card leads (ADR-150 points 4 and 5, addendum of 2026-10-29): the family's
 * detail page of the product, else the family's page for a family whose cards lead there (the
 * dataPixies' bundles), else the generic product page.
 */
export function cardTo(
  display: Pick<FamilyDisplay, "page" | "cards" | "detail">,
  product: { slug: string },
): RouteLocationRaw {
  if (display.detail) return { name: display.detail, params: { slug: product.slug } };
  if (display.cards === "page" && display.page) return { name: display.page.route };
  return { name: "product", params: { slug: product.slug } };
}

/**
 * The look of a shelf: the family's entry of the table, or the default under the family's label
 * from the API; the products without a family have no heading and the default.
 */
export function displayOf(family: ProductFamily | null): Omit<FamilyDisplay, "heading"> & { heading: string | null } {
  if (!family) return { ...DEFAULT_DISPLAY, heading: null };
  return FAMILY_DISPLAY.get(family.id) ?? { ...DEFAULT_DISPLAY, heading: family.name };
}

/**
 * The grid of a shelf by its columns, as whole class names (AGENTS.md): one column stays a
 * column, more fill from two on small screens up to the count on wide ones; four jump from two
 * to four, as the prototype's dataPixies.
 */
export const COLUMN_CLASSES: Readonly<Record<Columns, string>> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 xl:grid-cols-4",
};
